import React, { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { CDN2 } from '@/helpers/api';
import { Breadcrumb, Button, Input } from 'antd';
import { GetServerSideProps } from 'next';
import { basicPageProps, mobileProps } from '@/utils/isServer';
import Search from '@/components2/Search';
import { useRouter } from 'next/router';
import CouponContent from '@/components/CouponContent';
import FAQCoupon from '@/components/FAQCoupon';
import { modalType } from '@/components/Modal';
import SearchStore from '@/stores/SearchStore';
import { useMobxStores } from '@/stores';
import InputEmail from '@/components/InputEmail';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const id = ctx.query.id as string;

  const { isMobile, isIos } = mobileProps(ctx);
  const { airports, configInfo } = await basicPageProps(ctx);

  const airport = airports.result.find(
    (airport) =>
      airport.airport_initials.trim().toLowerCase() === id?.toLowerCase()
  );
  if (!airport) {
    return {
      redirect: {
        destination: '/parking-coupons',
        permanent: false,
      },
    };
  }

  return {
    props: {
      airports,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
      mobileType: isIos ? 'ios' : 'android',
    },
  };
};

function Coupon({ airports, configInfo, deviceType, mobileType, ...props }) {
  const router = useRouter();
  const id = router.query['id'] as string;
  const airport = airports.result.find(
    (airport) =>
      airport.airport_initials.trim().toLowerCase() == id.toLowerCase()
  )

  let selectedAirport = null;
  if (airport) {
    selectedAirport = JSON.stringify({
      value: `${airport.airport_initials} - ${airport.airport_name}`,
      id: airport.airport_id,
      airportinitials: airport.airport_initials,
    })
  }

  const airportName = airport?.airport_name;

  router.query.airport;

  let [city, region] = ['', ''];

  const spl = (airport?.airport_city ?? ',').split(', ');
  if (spl.length > 1) {
    city = spl[0];
    region = spl[1];
  }

  const cleanedName = (airportName ?? '')
    .replace(/(International|Regional|Airport|Parking)/gi, '')
    .trim();

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'On Air Parking',
    url: `https://onairparking.com/parking-coupons/${id}`,
    logo: 'https://onairparking.com/assets/public/image/logo_app.png',
    description: `Exclusive coupon to save $${configInfo.couponPageAppDiscount} on parking at ${airportName} (${id}) with On Air Parking.`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: region,
    },
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://onairparking.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Parking Coupons',
        item: 'https://onairparking.com/parking-coupons',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${cleanedName} Parking Coupon`,
        item: `https://onairparking.com/parking-coupons/${id}`,
      },
    ],
  };

  const {
    searchStore,
  }: {
    searchStore: SearchStore;
  } = useMobxStores();

  const [selectedDomain, setSelectedDomain] = useState('');
  const [email, setEmail] = useState<string>('');
  const handleSelect = (value) => {
    if (value.includes('@')) {
      setSelectedDomain(value);
      if (setEmail) {
        setEmail(value);
      }
    } else {
      setSelectedDomain(selectedDomain + value);
      if (setEmail) {
        setEmail(selectedDomain + value); // Append selected domain if missing
      }
    }
  };

  const doSubscribe = async () => {
    if (email) {
      await searchStore.subscribeRegular(2, email, '');
      modalType({
        type: 'success',
        title: 'You`re all set!',
        okButtonProps: {
          className: 'bg-primary',
        },
        content: (
          <p>
            Keep an eye on your inbox for the latest deals and exclusive promo
            codes. Happy saving!
          </p>
        ),
        onOk: () => {
          setEmail('');
        },
      });
      setEmail('');
    }
  };

  return (
    <div className="relative">
      <Head>
        <title>{`${cleanedName} Parking Coupon & Promo Code | Save $${configInfo.couponPageAppDiscount} at ${airportName} (${id}) – On Air Parking`}</title>
        <meta
          name="description"
          content={`Use our exclusive ${cleanedName} parking coupon & promo code (${id}${configInfo.couponPageSuffix}) to save $${configInfo.couponPageAppDiscount} on ${airportName} parking. Book your discount now with On Air Parking`}
        />
        <link
          rel="canonical"
          href={`https://www.onairparking.com/parking-coupons/${id}`}
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content={`${cleanedName} Parking Coupon & Promo Code | Save $${configInfo.couponPageAppDiscount} at ${airportName} (${id}) – On Air Parking`}
        />
        <meta
          property="og:description"
          content={`Use our exclusive ${cleanedName} parking coupon & promo code (${id}${configInfo.couponPageSuffix}) to save $${configInfo.couponPageAppDiscount} on ${airportName} parking. Book your discount now with On Air Parking`}
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
        <meta
          property="og:url"
          content={`https://www.onairparking.com/parking-coupons/${id}`}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaData),
          }}
        ></script>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbData),
          }}
        ></script>
      </Head>
      <div className="md:min-h-full relative">
        <div className="relative" style={{ backgroundColor: '#d0a785' }}>
          <Image
            src={CDN2() + '/staticmyapp/bg-old-landing-desktop.webp'}
            className="z-0 w-full object-cover min-h-64 h-full absolute object-bottom"
            alt="Airport Background"
            fill
            priority
            quality={70}
          />

          <section className="container mx-auto px-4 py-6 z-10 relative">
            <div
              className="flex flex-col justify-center text-center text-gray-600 mb-4"
              suppressHydrationWarning
            >
              <h1
                className={`text-2xl lg:text-4xl w-full text-center mb-4 font-bold text-gray-600 uppercase`}
              >
                {cleanedName} Parking Coupon & Promo Code ({id})
              </h1>
              <p className="text-base md:text-lg">
                Welcome to On Air Parking's {cleanedName} coupon page. No matter
                if you call it "{airportName}" or simply "{id}" our exclusive
                promo code gives you ${configInfo.couponPageAppDiscount} off
                parking.
              </p>
            </div>
            <Search searchType="airport" airports={airports} selectedAirport={selectedAirport} />
          </section>
        </div>
        <div className="container mx-auto px-4 py-6 z-10 relative">
          <Breadcrumb className="text-sm md:text-base mt-4 mb-8">
            <Breadcrumb.Item>
              <a href="/">Home</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <a href="/parking-coupons">Parking Coupons</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{cleanedName} Parking Coupon</Breadcrumb.Item>
          </Breadcrumb>

          <CouponContent
            airportName={cleanedName}
            configInfo={configInfo}
            id={id}
          />

          <section
            id={`exclusive-deals`}
            className="flex flex-col justify-center items-center bg-gray-50 p-6 mt-8"
          >
            <div className="max-w-lg space-y-4">
              <h2 className="text-xl lg:text-3xl font-bold uppercase text-center">
                Exclusive deals
              </h2>
              <p className="text-lg mb-4 text-center">
                Don't miss out on great discounts! Sign up, and we'll send the
                latest deals and promo codes directly to your inbox.
              </p>
              <div className="flex flex-row gap-4">
                <label className="sr-only" htmlFor="email-input">
                  Parking Location
                </label>
                <InputEmail
                  email={email}
                  inputId="email-input"
                  handleSelect={handleSelect}
                  setEmail={setEmail}
                />

                <Button type="primary" onClick={doSubscribe}>
                  SUBSCRIBE
                </Button>
              </div>
            </div>
          </section>
          <section
            id="about-atlanta-coupon"
            className="flex flex-row justify-between items-center md:gap-2 bg-orange-50 p-6 md:p-20 mt-8 relative overflow-hidden"
          >
            <div className="flex-1 space-y-4 md:pr-20 lg:pr-40">
              <h2 className="text-xl lg:text-3xl font-bold uppercase">
                Save on {cleanedName} Parking with On Air Parking
              </h2>
              <p className="text-lg">
                At On Air Parking, we make parking at {cleanedName} ({id})
                simple, affordable, and stress-free. We’ve partnered with
                trusted off-airport facilities to offer you convenient and
                budget-friendly parking just minutes from the terminal.
              </p>

              <p className="text-lg">
                Our rates are already some of the best available, but with our
                exclusive {id} parking coupons, you can save even more:
                <ul className="list-disc pl-5">
                  <li>
                    <b>Save up to ${configInfo.couponPageAppDiscount}</b> when
                    booking through the On Air Parking app—just enter the coupon
                    code at checkout.
                  </li>
                  <li>
                    <b>Save ${configInfo.couponPageWebDiscount}</b> when booking
                    on our website—apply the coupon code at checkout.
                  </li>
                </ul>
              </p>

              <p className="text-lg">
                <b>Important</b>: You must be logged in to use a coupon, and
                only one discount can be applied per booking—either through the
                app or the website.
              </p>

              <p className="text-lg">
                Enjoy a hassle-free parking experience with quick online
                reservations, free shuttle service to and from <b>{id}</b>, and
                secure parking. Take advantage of our exclusive promo codes and
                discover why off-airport parking is the perfect choice for
                budget-savvy travelers!
              </p>
            </div>
            <div className="hidden md:flex absolute -right-28 md:-right-40 w-56 h-56 md:w-80 md:h-80 rounded-full overflow-hidden">
              <Image
                src={`${CDN2()}/staticmyapp/coupons/coupons.jpg`}
                fill
                loading="lazy"
                alt={`${airportName} Airport (${id})`}
              />
            </div>
          </section>

          <section
            id="faq"
            className="flex flex-col md:flex-row gap-6 md:gap-10 lg:gap-20 mt-12"
          >
            <h2 className="font-bold uppercase text-xl lg:text-5xl lg:w-1/4 self-center">
              Frequently Asked Questions
            </h2>
            <FAQCoupon
              discountApp={configInfo.couponPageAppDiscount}
              discountWeb={configInfo.couponPageWebDiscount}
              airportInitial={id}
              airportName={airportName}
            />
          </section>

          <section
            id={`all-coupons`}
            className="flex flex-col justify-center items-center p-6 md:mt-8"
          >
            <div className="mt-8 text-center">
              <a
                href="/parking-coupons"
                title="View all airport parking coupons"
                className="text-lg lg:text-3xl font-bold uppercase bg-primary text-white border border-primary p-4 rounded-lg hover:bg-white hover:text-primary"
              >
                View All Parking Coupons
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Coupon;
